import { useSelector } from "react-redux";
import { cn } from "@/lib/utils";
import { IRootReducerState } from "@/store/reducers";
import { Calendar, Globe, Code, Database } from "lucide-react";

interface GeneralSettingsProps {
    className?: string;
}

interface ProjectDetailItem {
    label: string;
    value: string | undefined;
    icon: React.ReactNode;
    type?: 'text' | 'url' | 'date';
}

const GeneralSettings = ({ className }: GeneralSettingsProps) => {
    const { currentProjectDetails } = useSelector((state: IRootReducerState) => state.projectData);

    // Format project details for display
    const projectDetails: ProjectDetailItem[] = [
        {
            label: "Project Name",
            value: currentProjectDetails?.name || "Not specified",
            icon: <Code className="w-4 h-4" />,
            type: 'text'
        },
        {
            label: "Project ID",
            value: currentProjectDetails?.id || "Not available",
            icon: <Database className="w-4 h-4" />,
            type: 'text'
        },
        {
            label: "App Type",
            value: currentProjectDetails?.app_type ? currentProjectDetails.app_type.toUpperCase() : "Not specified",
            icon: <Code className="w-4 h-4" />,
            type: 'text'
        },
        {
            label: "Subdomain",
            value: currentProjectDetails?.subdomain || "Not deployed",
            icon: <Globe className="w-4 h-4" />,
            type: 'text'
        },
        {
            label: "Live URL",
            value: currentProjectDetails?.subdomain_url || "Not deployed",
            icon: <Globe className="w-4 h-4" />,
            type: 'url'
        },
        {
            label: "Created Date",
            value: currentProjectDetails?.created_at ? new Date(currentProjectDetails.created_at).toLocaleDateString() : "Not available",
            icon: <Calendar className="w-4 h-4" />,
            type: 'date'
        }
    ];

    const formatValue = (item: ProjectDetailItem) => {
        if (!item.value || item.value === "Not specified" || item.value === "Not deployed" || item.value === "Not available") {
            return <span className="text-[var(--muted-foreground)]">{item.value}</span>;
        }

        if (item.type === 'url' && item.value !== "Not deployed") {
            return (
                <a 
                    href={item.value} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[var(--primary)] hover:underline"
                >
                    {item.value}
                </a>
            );
        }

        return <span className="text-[var(--foreground)]">{item.value}</span>;
    };

    return (
        <div className={cn("settings-content", className)}>
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold text-[var(--foreground)] mb-2">Project Overview</h3>
                    <p className="text-sm text-[var(--muted-foreground)]">
                        View your project details and configuration
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {projectDetails.map((item, index) => (
                        <div key={index} className="p-4 rounded-lg border border-[var(--border)] bg-[var(--card)]">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="text-[var(--muted-foreground)]">
                                    {item.icon}
                                </div>
                                <h4 className="text-sm font-medium text-[var(--foreground)]">
                                    {item.label}
                                </h4>
                            </div>
                            <div className="text-sm">
                                {formatValue(item)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default GeneralSettings;
